<template>
	<div class="top-sales">
		<b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>
		<h1>Top Sales</h1>
		<div class="content-wrapper">
			<b-field label="Select Shop (optional)">
				<b-autocomplete
					v-model="shopsSuggestions"
					:data="filteredShops"
					:open-on-focus="true"
					placeholder="Shop"
					field="name"
					@select="option => (selectedShop = option)"
					class="shopAutoComplete"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</b-field>

			<div class="radioCheck">
				<b-radio v-model="topType" name="name" native-value="products">Top Sold Products</b-radio>
				<b-radio v-model="topType" name="name" native-value="brands">Top Sold Brands</b-radio>
			</div>

			<b-field label="Select Brand (required)" v-if="displayBrandSelection">
				<b-autocomplete
					v-model="brandSuggestions"
					:data="filteredBrands"
					:open-on-focus="true"
					placeholder="Brand"
					field="name"
					@select="option => (selectedBrand = option)"
					class="brandAutocomplete"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</b-field>

			<div class="datepickerWrapper">
				<b-field label="Starting Date">
					<b-datepicker :date-formatter="dateFormatter" placeholder="Click to select..." v-model="startingDate" icon="calendar-today">
						<button class="button is-primary" @click="startingDate = new Date()">
							<b-icon icon="calendar-today"></b-icon>
							<span>Today</span>
						</button>

						<button class="button is-danger" @click="startingDate = null">
							<b-icon icon="close"></b-icon>
							<span>Clear</span>
						</button>
					</b-datepicker>
				</b-field>

				<b-field label="Ending Date">
					<b-datepicker
						:date-formatter="dateFormatter"
						placeholder="Click to select..."
						v-model="endingDate"
						icon="calendar-today"
						:min-date="minDate"
						:max-date="maxDate"
					>
						<button class="button is-danger" @click="endingDate = null">
							<b-icon icon="close"></b-icon>
							<span>Clear</span>
						</button>
					</b-datepicker>
				</b-field>
			</div>

			<div class="filter-button-wrapper">
				<div class="big-button" @click="submit">SUBMIT</div>
			</div>
		</div>

		<div class="total-sales" v-if="totalSold > 0">
			<div class="total-wrapper">
				<div class="total-amount">
					<span>Total Sales (Estimated)</span>
					<br />
					{{ totalSold.toLocaleString('en-GB') }}
				</div>

				<div class="total-amount">
					<span>Total Profit (Estimated)</span>
					<br />
					{{ totalProfit.toLocaleString('en-GB') }}
				</div>

				<div class="total-orders" v-if="totalOrders > 0">
					<span>Total Orders</span>
					<br />
					{{ totalOrders.toLocaleString('en-GB') }}
				</div>
				<div class="total-orders" v-else>
					<span>Total Sold Items</span>
					<br />
					{{ totalItems.toLocaleString('en-GB') }}
				</div>
			</div>
		</div>

		<div v-if="topSoldProducts.length > 0" class="top-sold-wrapper">
			<h3>Top 50 sold products</h3>

			<div class="filter-button-wrapper">
				<div class="big-button" @click="exportTopSoldProducts()">EXPORT</div>
			</div>

			<div class="top-sold-table header">
				<div>Name</div>
				<div>BD Code</div>
				<div>Brand</div>
				<div>Sold Quantity</div>
			</div>
			<div v-for="(data, index) of topSoldProducts" v-bind:key="index" class="top-sold-table">
				<div class="text-left">{{ data.name }}</div>
				<div>{{ data.bd_code }}</div>
				<div>{{ data.brand }}</div>
				<div>{{ data.qty }}</div>
			</div>
		</div>

		<div v-if="topSoldBrands.length > 0" class="top-sold-wrapper">
			<h3>Top 50 sold brands</h3>

			<div class="filter-button-wrapper">
				<div class="big-button" @click="exportTopSoldBrands()">EXPORT</div>
			</div>

			<div class="top-sold-brand-table header">
				<div>Brand Name</div>
				<div>Brand Code</div>
				<div>Sold Quantity</div>
			</div>
			<div v-for="(data, index) of topSoldBrands" v-bind:key="index" class="top-sold-brand-table">
				<div class="text-left">{{ data.brand }}</div>
				<div>{{ data.code }}</div>
				<div>{{ data.qty }}</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'TopSales',
	data: () => ({
		isLoading: false,
		totalSales: [],
		startingDate: null,
		endingDate: null,
		minDate: null,
		maxDate: null,
		shops: [], // For list of shops from API
		shopsSuggestions: '', // For dropdown selection
		selectedShop: null, // Selected shop
		topSoldProducts: [], // To handle top sold products
		topSoldBrands: [], // To handle top sold brands
		topType: 'products', // For top type export
		displayBrandSelection: true,
		brands: [], // To store brands
		selectedBrand: null,
		brandSuggestions: '',
		totalSold: 0,
		totalProfit: 0,
		totalItems: 0,
		totalOrders: 0
	}),

	watch: {
		startingDate: function(data) {
			if (data !== null) {
				this.minDate = data
				this.maxDate = new Date(data.getFullYear(), data.getMonth(), data.getDate() + 30)

				this.endingDate = data
			} else {
				this.endingDate = null
			}
		},

		// To display or hide brand selection
		topType: function(newData) {
			if (newData == 'products') {
				this.displayBrandSelection = true
			} else {
				this.displayBrandSelection = false
			}
		}
	},

	mounted: async function() {
		await this.getShops()

		await this.getBrands()
	},

	computed: {
		getTotalProfit() {
			const totalProfit = this.totalSales.pay_amount - this.totalSales.original_no_promotion_total_price

			return totalProfit.toLocaleString('en-GB')
		},

		filteredBrands() {
			return this.brands.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.brandSuggestions.toLowerCase()) >= 0
				)
			})
		},

		filteredShops() {
			return this.shops.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.shopsSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	methods: {
		// Get all the brands
		getBrands: async function() {
			const data = {
				limit: 2000,
				sort: {
					name: 'asc'
				}
			}
			const apiGetBrands = await this.$api.getBrand({ data })

			if (apiGetBrands !== undefined) {
				this.brands = apiGetBrands.data.list
				this.brands.unshift({ code: 'ALL', name: 'ALL', _id: 'all' })
			}
		},

		// Get list of shops
		getShops: async function() {
			const apiGetShops = await this.$api.getShops({})

			this.shops = apiGetShops.data.list
		},

		exportTopSoldBrands: function() {
			let export_csv = 'BRAND NAME,BRAND CODE,SOLD QUANTITY\r\n'

			this.topSoldBrands.map(item => {
				const toAdd = [
					'"' + item.brand + '"', // Barcode
					item.code, // Product Name
					item.qty
				] // Net Amount

				const row = toAdd.join(',')

				export_csv += row + '\r\n'
			})

			let filename_date = this.$moment().format('DDMMYYhhmmss')

			var csvData = new Blob([export_csv], { type: 'text/csv' }) //new way
			var csvUrl = URL.createObjectURL(csvData)

			var link = document.createElement('a')
			link.setAttribute('href', csvUrl)
			link.setAttribute('download', 'Top Sold Brands ' + filename_date + '.csv')
			document.body.appendChild(link) // Required for FF

			link.click()
			link.remove()
		},

		exportTopSoldProducts: function() {
			let export_csv = 'NAME,BD CODE,BARCODE,BRAND,SOLD QUANTITY,TOTAL AMOUNT\r\n'

			this.topSoldProducts.map(item => {
				const toAdd = [
					'"' + item.name + '"', // Barcode
					item.bd_code, // Product Name
					item.barcode,
					item.brand,
					item.qty,
					item.totalAmount
				] // Net Amount

				const row = toAdd.join(',')

				export_csv += row + '\r\n'
			})

			let filename_date = this.$moment().format('DDMMYYhhmmss')

			var csvData = new Blob([export_csv], { type: 'text/csv' }) //new way
			var csvUrl = URL.createObjectURL(csvData)

			var link = document.createElement('a')
			link.setAttribute('href', csvUrl)
			link.setAttribute('download', 'Top Sold Product ' + filename_date + '.csv')
			document.body.appendChild(link) // Required for FF

			link.click()
			link.remove()
		},

		submit: async function() {
			if (this.startingDate == null || this.endingDate == null) {
				this.$swal.fire('Shop, starting and ending dates are required')

				return
			}

			if (this.topType === 'products' && this.selectedBrand == null) {
				this.$swal.fire('Please select 1 brand or "ALL"')

				return
			}

			var data = {}
			const createdAt = {}
			this.topSoldProducts = []
			this.topSoldBrands = []
			this.totalSales = []
			this.isLoading = true
			var loop = true
			var loopCount = 0
			this.totalSold = 0
			this.totalProfit = 0
			this.totalItems = 0
			this.totalOrders = 0

			if (this.startingDate !== null) {
				createdAt['$gte'] = this.$moment(this.startingDate)
					.set({ hour: 0 })
					.format('x')
			}

			if (this.endingDate !== null) {
				createdAt['$lte'] = this.$moment(this.endingDate)
					.set({ hour: 24 })
					.format('x')
			}

			data = {
				pay_status: 1,
				time: {
					createdAt
				}
			}

			if (this.selectedShop && this.selectedShop._id) {
				data.shop = this.selectedShop._id
			}

			if (this.selectedBrand._id == 'all') {
				const getSales = await this.$api.getSales({ data })

				if (getSales) {
					this.totalSold = getSales.data[0].total_price
					this.totalProfit = this.totalSold * 0.25
					this.totalOrders = getSales.data[0].count
				} else {
					this.$swal.fire('No data!', 'No data for selected parameters!', 'info')
				}
				this.isLoading = false
			} else {
				data.order_items = { brand: this.selectedBrand._id }
				data.limit = 1000
				data.sort = {
					qty: -1
				}

				while (loop) {
					if (loopCount > 0) {
						data.skip = loopCount * 1000
					}

					const apiGetSales = await this.$api.getTopProducts({ data })

					if (apiGetSales !== undefined && apiGetSales.data.count > 0) {
						apiGetSales.data.list.map(sales => {
							this.totalSales.push(sales)
						})
					}

					if (apiGetSales.data.count == 0) {
						loop = false
						this.$swal.fire('No data!', 'No data for selected parameters!', 'info')
					}

					loop = apiGetSales.data.count < 1000 ? false : true

					loopCount++
				}

				if (this.totalSales.length > 0) {
					this.totalSales.map(items => {
						items.order_items.map(item => {
							// var price = item.product.price * item.qty
							var price = item.total_price
							var profit = 0

							// if (item.promotions.length > 0) {
							// 	let promotion = item.promotions[0]

							// 	if (promotion.type == 'discount_product_percentage') {
							// 		console.log(promotion)

							// 		price = item.product.price * (promotion.condition.percentage / 100) * item.qty

							// 		console.log(price)
							// 	}
							// }

							if (item.product.commission) {
								profit = price * (item.product.commission / 100)
							} else {
								profit = price - item.product.original_price
							}

							this.totalSold += price
							this.totalProfit += profit
							this.totalItems += item.qty
						})
					})
				}
			}

			if (this.totalSales) {
				if (this.topType == 'products') {
					// For getting top 50 products
					var topSoldProducts = {
						limit: 50,
						time: {
							createdAt
						},
						sort: {
							qty: -1
						}
					}

					if (this.selectedShop && this.selectedShop._id) {
						topSoldProducts.shop = this.selectedShop._id
					}

					if (this.selectedBrand._id !== 'all') {
						topSoldProducts.order_items = { brand: this.selectedBrand._id }
					}

					const apiTopProducts = await this.$api.getTopProducts({ data: topSoldProducts })

					// Loop top sold items
					if (apiTopProducts !== undefined && apiTopProducts.data.count > 0) {
						// Assign list
						let list = apiTopProducts.data.list
						// Loop all the items and push state
						list.map(data => {
							let values = data.order_items[0].product

							let totalAmount = 0

							data.order_items.map(order_item => {
								totalAmount += order_item.total_price
							})

							const matchedBrand = this.brands.filter(brand => {
								return brand._id == values.brand
							})

							let toAdd = {
								name: values.name,
								bd_code: values.bd_code,
								barcode: values.barcode,
								qty: data.qty,
								totalAmount,
								brand: matchedBrand[0].name
							}

							this.topSoldProducts.push(toAdd)
						})
					}
				} else if (this.topType == 'brands') {
					var topSoldBrands = {
						limit: 50,
						time: {
							createdAt
						},
						sort: {
							qty: -1
						}
					}

					if (this.selectedShop && this.selectedShop._id) {
						topSoldBrands.shop = this.selectedShop._id
					}

					const apiTopBrands = await this.$api.getTopBrands({ data: topSoldBrands })

					// Loop top brands
					if (apiTopBrands !== undefined && apiTopBrands.data.count > 0) {
						// Assign list
						let list = apiTopBrands.data.list
						// Loop all the items and push state
						list.map(data => {
							let values = data.order_items[0].product

							const matchedBrand = this.brands.filter(brand => {
								return brand._id == values.brand
							})

							let toAdd = {
								qty: data.qty,
								brand: matchedBrand[0].name,
								code: matchedBrand[0].code
							}

							this.topSoldBrands.push(toAdd)
						})
					}
				}
			}
			this.isLoading = false
		},

		getSales: async function() {
			const data = {}

			const apiGetSales = await this.$api.getSales({ data })
			if (apiGetSales !== undefined) {
				this.totalSales = apiGetSales.data[0]
			}
		},

		// For calender format
		dateFormatter(dt) {
			return dt.toLocaleDateString('en-GB')
		}
	}
}
</script>

<style scoped>
@import '../styles/GeneralStyles.css';
</style>
